/**
 * The ET bar (PLAN-FINAL B7.1 layer 1) as plain data, built from the shell answer. Pure, so the
 * rules are unit-tested without rendering:
 *
 * - The launcher lists the customer's accounts as discovery gives them (D31 carry-over: the target
 *   product refuses a person it has no link for, `person_not_linked`). A tile is a link only when
 *   discovery gives it an https URL; this product's own tile is marked current.
 * - The customer logo is used only when it is an https URL.
 * - A connected-capability session (the customer set an accent or logo, or the person arrived from
 *   a host product, so there is a return link): the bar says "HR & Payroll" and the context row
 *   carries the "HR & Payroll by AnooreHR" co-brand line (i18n). A direct customer with no brand:
 *   AnooreHR's own name and colour, and no mark (the no-brand variant). KR3 Q81, 2026-10-25.
 * - Another product's tile shows Console's brand (discovery `launcher[].brand`, contract E34):
 *   its https logo, else Console's monogram on its colour. Only an old Console without `brand`
 *   falls back to the local, camelCase-aware `initials()` (the same rule Console uses).
 * - The return link comes only from the shell's `return_link`; it is a link only with an https
 *   URL, otherwise plain text (never a dead link).
 * - Other products are named only by what discovery says; nothing here names one in code.
 */
import i18n from 'i18next';
import { brandTokens, contrast, parseHex, type BrandTokens } from './brand';
import { SELF } from './config';
import type { InterplayShell } from './types';

export interface LauncherTile {
  key: string;
  name: string;
  sub: string;
  href: string | null;
  current: boolean;
  readOnly: boolean;
  /** Image: this product's own bundled mark, or another product's https logo from Console. */
  markSrc: string | null;
  monogram: string;
  /** The monogram's fill and ink from Console's brand colour; null → the neutral token. */
  monoColours: { bg: string; fg: string } | null;
}

export interface EtBarModel {
  customerName: string;
  customerInitials: string;
  logoUrl: string | null;
  coBranded: boolean;
  brand: BrandTokens;
  productLabel: string;
  productMarkSrc: string;
  tiles: LauncherTile[];
  /** "Back to <host>": a link with an https URL, plain text while the URL is null (Console E31). */
  returnLink: { label: string; href: string | null } | null;
  /** The co-brand line: `capability` bold, then `byProvider` ("by AnooreHR"); `line` is both. */
  mark: { capability: string; provider: string; byProvider: string; line: string } | null;
  /** The context row (return link and/or mark) is shown. */
  hasContextRow: boolean;
  stale: boolean;
  /** The customer's sector from the host, for the term map (Q50). */
  sector: string | null;
}

const MAX_TILES = 24;

export function httpsUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048) return null;
  try {
    const u = new URL(value);
    if (u.protocol !== 'https:' || !u.hostname || u.username || u.password) return null;
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Two-letter monogram, the same rule as Console's (KR3 Q81): (1) a name with two or more capital
 * letters gives its first two capitals ("TalentScholarPlus" → "TS", "AnooreHR" → "AH"); (2) else
 * the initials of the first two words; (3) else the first two letters, upper-cased.
 */
export function initials(name: string): string {
  const clean = name.replace(/[^\p{L}\p{N}\s]/gu, ' ');
  const caps = clean.match(/\p{Lu}/gu) ?? [];
  if (caps.length >= 2) return caps.slice(0, 2).join('');
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const picked = words.length === 1 ? [words[0][0], words[0][1] ?? ''] : [words[0][0], words[1][0]];
  return picked.join('').toUpperCase();
}

/**
 * The app's i18n (`interplay.*` keys in `src/i18n/*.json`, initialised by `@/i18n/config` in
 * App.tsx). Before i18next is initialised (a unit test, a very early call) the English default is
 * interpolated here, so the model never shows a raw key.
 */
export function tr(key: string, fallback: string, vars: Record<string, string> = {}): string {
  if (i18n.isInitialized) {
    const out = i18n.t(key, { ...vars, defaultValue: fallback });
    if (typeof out === 'string' && out && out !== key) return out;
  }
  return fallback.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => vars[k] ?? '');
}

const BRAND_COLOUR = /^#[0-9a-f]{6}$/i;
const BRAND_MONOGRAM = /^[A-Z0-9]{1,2}$/;

export interface ProductBrand {
  colour: string | null;
  logoUrl: string | null;
  monogram: string;
}

/** Console's brand for a launcher entry, or null when absent or not the contract's shape. */
export function productBrand(value: unknown): ProductBrand | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const colour = v.colour ?? null;
  const logo = v.logo_url ?? null;
  if (typeof v.monogram !== 'string' || !BRAND_MONOGRAM.test(v.monogram)) return null;
  if (colour !== null && !(typeof colour === 'string' && BRAND_COLOUR.test(colour))) return null;
  const logoUrl = logo === null ? null : httpsUrl(logo);
  if (logo !== null && !logoUrl) return null;
  return { colour: colour === null ? null : (parseHex(colour as string) as string), logoUrl, monogram: v.monogram };
}

/** White or near-black ink on a monogram fill, whichever reads better. */
function monoColours(colour: string | null): { bg: string; fg: string } | null {
  if (!colour) return null;
  const fg = contrast(colour, '#FFFFFF') >= contrast(colour, '#111827') ? '#FFFFFF' : '#111827';
  return { bg: colour, fg };
}

function clip(value: unknown, max: number): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

export interface BuildOptions {
  /**
   * The tenant's own company colour. Used for the bar's tokens only when the customer set no
   * brand (not co-branded), and only if it reaches AA; otherwise AnooreHR's own brand.
   */
  companyColour?: string | null;
}

export function buildEtBarModel(
  shell: InterplayShell | null | undefined,
  opts: BuildOptions = {},
): EtBarModel | null {
  if (!shell || shell.object !== 'interplay_shell') return null;
  const brandIn = shell.customer?.brand ?? null;
  const customerName =
    clip(brandIn?.display_name, 200) || clip(shell.customer?.name, 200) || 'Your organisation';
  const logoUrl = httpsUrl(brandIn?.logo_url);
  const accent = parseHex(brandIn?.accent_colour ?? null);
  const coBranded = Boolean(accent || logoUrl);
  const brand = coBranded ? brandTokens(accent) : brandTokens(opts.companyColour ?? null);

  const rl = shell.return_link;
  const rlHref = rl ? httpsUrl(rl.url) : null;
  const returnLink =
    rl && clip(rl.product_name, 120)
      ? { label: tr('interplay.back_to', 'Back to {{product}}', { product: clip(rl.product_name, 120) }), href: rlHref }
      : null;
  // A connected-capability session: co-branded, or reached from a host product (Q81, screen 18).
  const connected = coBranded || returnLink !== null;
  const capabilityLabel = tr('interplay.capability_label', 'HR & Payroll');
  const byProvider = tr('interplay.by_product', 'by {{product}}', { product: SELF.name });

  const selfCode = shell.account?.product_code ?? null;
  const tiles: LauncherTile[] = (Array.isArray(shell.launcher) ? shell.launcher : [])
    .filter((it) => it && typeof it.product_code === 'string' && typeof it.product_name === 'string')
    .slice(0, MAX_TILES)
    .map((it) => {
      const current = selfCode !== null && it.product_code === selfCode;
      const productName = clip(it.product_name, 120);
      const name = current && connected ? capabilityLabel : productName;
      const sub = current && connected ? byProvider : clip(it.account_name, 120) || customerName;
      // Another product's mark comes from Console; never guessed locally while `brand` is there.
      const pb = current ? null : productBrand(it.brand);
      return {
        key: it.product_code,
        name,
        sub,
        href: current ? '/' : httpsUrl(it.url ?? it.home_url),
        current,
        readOnly: it.state === 'read_only',
        markSrc: current ? SELF.markSrc : pb?.logoUrl ?? null,
        monogram: pb?.monogram ?? initials(productName),
        monoColours: monoColours(pb?.colour ?? null),
      };
    });

  const mark = connected
    ? {
        capability: capabilityLabel,
        provider: SELF.name,
        byProvider,
        line: tr('interplay.co_brand', '{{capability}} by {{product}}', { capability: capabilityLabel, product: SELF.name }),
      }
    : null;

  return {
    customerName,
    customerInitials: initials(customerName),
    logoUrl,
    coBranded,
    brand,
    productLabel: connected ? capabilityLabel : SELF.name,
    productMarkSrc: SELF.markSrc,
    tiles,
    returnLink,
    mark,
    hasContextRow: Boolean(returnLink || mark),
    stale: Boolean(shell.stale),
    sector: clip(shell.sector, 64) || null,
  };
}

/**
 * The contrast-safe primary colour for the app theme (the existing brand path), or undefined.
 * Only a co-branded bar (the customer set an accent or logo through Console) drives the theme;
 * if its accent is unusable the theme takes the same AnooreHR fallback as the bar, so they agree.
 * Not co-branded (e.g. the default shell for a tenant not connected through Console): undefined,
 * so the tenant's own company colour keeps driving the theme exactly as with the flag off.
 */
export function themeAccent(model: EtBarModel | null, dark: boolean): string | undefined {
  if (!model || !model.coBranded) return undefined;
  return dark ? model.brand.dark.fill : model.brand.light.fill;
}
